import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText, Search, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermissions } from '@/common/hooks/usePermissions';
import { invoicesService, type ProviderFilter } from '@/features/invoices/services/invoices.service';
import { validateListSearch } from '@/features/invoices/invoice-search';
import { MonthSelect } from '@/features/invoices/components/MonthSelect';
import { formatDate, formatMonthCapitalized } from '@/features/invoices/components/invoice-formatters';
import { useDebouncedValue } from '@/features/invoices/hooks/useDebouncedValue';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_authenticated/admin/invoices/submitted/')({
  validateSearch: validateListSearch,
  component: AdminInvoicesSubmittedPage,
});

function AdminInvoicesSubmittedPage() {
  const { referenceMonth, filter = 'all', page = 1, search } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/invoices/submitted/' });
  const { hasPermission } = usePermissions();
  const canViewEmployee = hasPermission('employees.view');
  const [searchInput, setSearchInput] = useState(search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput);

  useEffect(() => {
    const next = debouncedSearch.trim() || undefined;
    if (next !== (search ?? undefined)) {
      navigate({ search: (prev) => ({ ...prev, search: next, page: undefined }), replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data: stats } = useQuery({
    queryKey: ['invoice-stats', referenceMonth || 'open'],
    queryFn: () => invoicesService.getStats(referenceMonth),
    placeholderData: (prev) => prev,
  });
  const activeMonth = referenceMonth ?? stats?.referenceMonth ?? '';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['invoice-providers', filter, activeMonth, page, search ?? ''],
    queryFn: () =>
      invoicesService.getProviders({
        referenceMonth: activeMonth,
        filter,
        page,
        limit: PAGE_SIZE,
        search,
      }),
    enabled: !!activeMonth,
    placeholderData: (prev) => prev,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/admin/invoices" search={activeMonth ? { referenceMonth: activeMonth } : {}}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Gestão de Notas
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Prestadores e Notas</h1>
          <p className="text-muted-foreground">
            Situação de envio em {activeMonth ? formatMonthCapitalized(activeMonth) : '...'}
          </p>
        </div>
        <MonthSelect
          value={activeMonth}
          onChange={(month) =>
            navigate({ search: (prev) => ({ ...prev, referenceMonth: month, page: undefined }) })
          }
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Prestadores PJ
              </CardTitle>
              <CardDescription>{total} prestador(es) encontrado(s)</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar nome, e-mail ou CNPJ"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-[260px] pl-9"
                />
              </div>
              <Select
                value={filter}
                onValueChange={(value) =>
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      filter: value === 'all' ? undefined : (value as ProviderFilter),
                      page: undefined,
                    }),
                  })
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Situação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="submitted">Com nota</SelectItem>
                  <SelectItem value="missing">Sem nota</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && !data ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError && !data ? (
            <div className="text-center py-8 space-y-3 text-muted-foreground">
              <p>Não foi possível carregar a lista.</p>
              <Button variant="outline" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>Nenhum prestador encontrado para os filtros informados.</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prestador</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>Último envio</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((provider) => {
                    const name = provider.fullName || provider.email;
                    return (
                      <TableRow key={provider.userId}>
                        <TableCell>
                          <div className="font-medium">
                            {canViewEmployee ? (
                              <Link
                                to="/employees/$id"
                                params={{ id: provider.userId }}
                                className="hover:underline"
                              >
                                {name}
                              </Link>
                            ) : (
                              name
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{provider.email}</div>
                        </TableCell>
                        <TableCell>{provider.cnpj || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={provider.submitted ? 'default' : 'secondary'}>
                            {provider.submitted
                              ? `${provider.invoiceCount} nota${provider.invoiceCount !== 1 ? 's' : ''}`
                              : 'Sem nota'}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(provider.lastSubmittedAt)}</TableCell>
                        <TableCell className="text-right">
                          <Button asChild variant="ghost" size="sm">
                            <Link
                              to="/admin/invoices/submitted/$userId"
                              params={{ userId: provider.userId }}
                              search={{
                                from: 'list',
                                referenceMonth: activeMonth || undefined,
                                filter: filter === 'all' ? undefined : filter,
                                listPage: page > 1 ? page : undefined,
                                listSearch: search,
                              }}
                            >
                              Ver notas
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Pagination
                total={total}
                page={page}
                limit={PAGE_SIZE}
                onPageChange={(next) =>
                  navigate({ search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }) })
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
